import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Clock, Sparkles, X } from 'lucide-react';

/**
 * TrialExpiredModal
 * Shown when a backend endpoint returns 402 with code=trial_expired.
 * Triggered via window event 'trial-expired' carrying { trial_expires_at, message, upgrade_url }.
 */
export default function TrialExpiredModal({ open, onClose, payload }) {
  const expired = payload?.trial_expires_at;
  const message = payload?.message || 'Your 14-day free trial has ended.';
  const upgradeUrl = payload?.upgrade_url || '/#pricing';

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent
        className="bg-[#0A0A0A] border border-amber-500/30 max-w-md rounded-sm"
        data-testid="trial-expired-modal"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-amber-400 font-['Outfit']">
            <Clock className="w-5 h-5" />Trial Ended
          </DialogTitle>
          <DialogDescription className="text-zinc-300 mt-2">
            {message}
          </DialogDescription>
        </DialogHeader>

        {expired && (
          <div className="text-xs text-zinc-500 font-['JetBrains_Mono'] bg-[#050505] border border-[#27272A] rounded-sm p-2">
            Expired: {new Date(expired).toLocaleDateString()} at {new Date(expired).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </div>
        )}

        <div className="space-y-2 text-sm text-zinc-400">
          <p>You can still:</p>
          <ul className="list-disc list-inside ml-2 space-y-1 text-zinc-500">
            <li>Browse the homepage + view existing data</li>
            <li>Apply to the beta tester program for free continued access</li>
            <li>Upgrade to a paid plan for unlimited use</li>
          </ul>
        </div>

        <div className="flex gap-2 flex-wrap mt-2">
          <Button
            className="bg-[#FF5E1A] hover:bg-[#FF7A3D] text-black rounded-sm flex-1"
            onClick={() => { window.location.href = upgradeUrl; onClose(); }}
            data-testid="trial-modal-upgrade"
          >
            <Sparkles className="w-4 h-4 mr-2" />Upgrade Plan
          </Button>
          <Button
            variant="outline"
            className="border-purple-500/40 text-purple-300 hover:bg-purple-500/10 rounded-sm flex-1"
            onClick={() => { window.location.href = '/#beta'; onClose(); }}
            data-testid="trial-modal-apply-beta"
          >
            Apply as Beta
          </Button>
        </div>
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-zinc-500 hover:text-zinc-300"
          aria-label="Close"
          data-testid="trial-modal-close"
        >
          <X className="w-4 h-4" />
        </button>
      </DialogContent>
    </Dialog>
  );
}
